/*
 * Copyright (c) 2024 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain
 * proprietary information of OCLC, Inc. and shall not be disclosed in whole or in
 * any part to any third party or used by any person for any purpose, without written
 * consent of OCLC, Inc.  Duplication of any portion of these  materials shall include this notice.
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import { Helmet } from 'react-helmet-async';
import { Box, Container } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { withStyles } from '@mui/styles';

import Header from '../Header';
import Footer from '../Footer';

import { fav, fav16, fav32, fav180, fav192, fav512 } from '../../images/fav';

const styles = {
  container: {
    display: 'flex',
    minHeight: '100%',
    flexDirection: 'column',
  },

  main: {
    flex: 1,
    padding: '16px 0',
  },
};

const messages = defineMessages({
  defaultPageTitle: {
    id: 'App.name',
    defaultMessage: 'Authentication Managed User UI',
  },
});

const Layout = ({
  classes,
  children,
  intl: { formatMessage },
  pageTitle,
  branding,
  userDisplayName,
  ...otherProps
}) => {
  const theme = useTheme();

  return (
    <Box
      className={classes.container + ' ' + otherProps.className}
      rowGap={4}
      sx={{
        bgcolor: theme.palette.grey.grey100,
        ...otherProps.boxStyles,
      }}
    >
      <Helmet>
        <title>{pageTitle || formatMessage(messages.defaultPageTitle)}</title>

        <link rel='icon' href={fav} type='image/x-icon' />
        <link rel='icon' href={fav16} sizes='16x16' type='image/png' />
        <link rel='icon' href={fav32} sizes='32x32' type='image/png' />

        {/* Apple touch icon (iOS devices)*/}
        <link rel='apple-touch-icon' sizes='180x180' href={fav180} />

        {/* Android Chrome icon (Progressive Web Apps) */}
        <link rel='icon' sizes='192x192' href={fav192} />
        <link rel='icon' sizes='512x512' href={fav512} />
      </Helmet>
      <Header branding={branding} userDisplayName={userDisplayName} />
      <main className={classes.main}>
        <Container
          fixed
          sx={{
            paddingLeft: { xs: 0 },
            paddingRight: { xs: 0 },
            maxWidth: { xs: '600px' },
            ...otherProps.containerStyles,
          }}
        >
          {children}
        </Container>
      </main>
      <Footer />
    </Box>
  );
};

Layout.propTypes = {
  branding: PropTypes.object,
  children: PropTypes.node.isRequired,
  intl: PropTypes.object.isRequired,
  classes: PropTypes.object,
  boxStyles: PropTypes.object,
  pageTitle: PropTypes.string,
  userDisplayName: PropTypes.string,
  otherProps: PropTypes.object,
};

export default injectIntl(withStyles(styles)(Layout));
